import React, { useRef, useState } from 'react';
import {
  motion,
  useScroll,
  useMotionValueEvent,
  AnimatePresence,
} from 'framer-motion';
import { EASE } from '../../../lib/motion';
import { WORKFLOW_CHAPTERS } from '../data';
import SectionMasthead from './SectionMasthead';
import ChapterVisual from './ChapterVisual';
import styles from '../Home.module.css';

/* ─── Workflow: pinned scroll deck (desktop, motion allowed) ───
   Scrolling through the tall track swipes topic + visual together.
   The animated deck is aria-hidden; the sr-only list below carries
   the same content for assistive tech. Chapters are marked by their
   phase + scene, never a numeral — the only numbered spine on the
   page is the section mastheads (this section is 02). */
const deckSlideVariants = {
  enter: (dir: number) => ({ opacity: 0, y: 64 * dir }),
  centre: { opacity: 1, y: 0 },
  exit: (dir: number) => ({ opacity: 0, y: -64 * dir }),
};

const WorkflowDeck: React.FC = () => {
  const trackRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState(0);
  const dirRef = useRef(1);

  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ['start start', 'end end'],
  });

  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    const n = WORKFLOW_CHAPTERS.length;
    const idx = Math.min(n - 1, Math.max(0, Math.floor(v * n)));
    setActive((prev) => {
      if (idx !== prev) dirRef.current = idx > prev ? 1 : -1;
      return idx;
    });
  });

  /** Scroll the page so the track progress centres chapter i. */
  const jumpTo = (i: number) => {
    const el = trackRef.current;
    if (!el) return;
    const top = window.scrollY + el.getBoundingClientRect().top;
    const scrollable = el.offsetHeight - window.innerHeight;
    window.scrollTo({
      top: top + (scrollable * (i + 0.5)) / WORKFLOW_CHAPTERS.length,
      behavior: 'smooth',
    });
  };

  const chapter = WORKFLOW_CHAPTERS[active];

  return (
    <section
      id="workflow"
      ref={trackRef}
      className={styles.deckTrack}
      aria-labelledby="workflow-title"
    >
      <div className={styles.deckSticky}>
        <SectionMasthead
          index="02"
          label="The workflow"
          titleId="workflow-title"
          title={
            <>
              From first enquiry
              <br />
              to final gallery.
            </>
          }
        />

        <div className={styles.deckBody} aria-hidden="true">
          <div className={styles.deckRail}>
            {/* Scroll progress along the rail hairline — a motion value,
                so it never re-renders React */}
            <motion.span
              className={styles.railProgress}
              style={{ scaleY: scrollYProgress }}
            />
            {/* Rail jump is a mouse-only convenience, intentionally NOT
                focusable: it lives inside the aria-hidden decorative deck, so
                a focusable button here would be a focusable-inside-aria-hidden
                anti-pattern. Keyboard/AT users reach every chapter two ways —
                scrolling the track advances `active` through all of them, and
                the full chapter content is exposed in the sr-only list below. */}
            {WORKFLOW_CHAPTERS.map((c, i) => (
              <div
                key={c.scene}
                role="presentation"
                className={`${styles.railItem} ${i === active ? styles.railItemActive : ''}`}
                onClick={() => jumpTo(i)}
              >
                <span className={styles.railText}>
                  <span className={styles.railPhase}>{c.phase}</span>
                  <span className={styles.railScene}>{c.scene}</span>
                </span>
              </div>
            ))}
          </div>

          <div className={styles.deckSlides}>
            <AnimatePresence mode="popLayout" custom={dirRef.current}>
              <motion.div
                key={active}
                className={styles.deckSlide}
                custom={dirRef.current}
                variants={deckSlideVariants}
                initial="enter"
                animate="centre"
                exit="exit"
                transition={{ duration: 0.45, ease: EASE }}
              >
                <div className={styles.deckSlideText}>
                  <p className={styles.chapterKicker}>
                    <span className={styles.chapterKickerIndex}>
                      {chapter.scene}
                    </span>
                    <span className={styles.chapterKickerRule} />
                    {chapter.kicker}
                  </p>
                  <h3 className={styles.chapterTitle}>{chapter.title}</h3>
                  <p className={styles.chapterBody}>{chapter.body}</p>
                  <ul className={styles.chapterLedger}>
                    {chapter.details.map((detail) => (
                      <li key={detail} className={styles.chapterLedgerRow}>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={styles.deckSlideVisual}>
                  <figure className={styles.captionedFigure}>
                    <ChapterVisual chapter={chapter} eager decorative />
                    <figcaption className={styles.frameCaption}>
                      {chapter.frame}
                    </figcaption>
                  </figure>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Full chapter content for assistive tech (deck above is decorative) */}
      <ul className="sr-only">
        {WORKFLOW_CHAPTERS.map((c) => (
          <li key={c.scene}>
            <h3>
              {c.scene} — {c.title}
            </h3>
            <p>{c.body}</p>
            <ul>
              {c.details.map((detail) => (
                <li key={detail}>{detail}</li>
              ))}
            </ul>
          </li>
        ))}
        <li>
          <p>
            Want to see the delivery for yourself? The demo gallery in the
            Client galleries section below is a full working example.
          </p>
        </li>
      </ul>
    </section>
  );
};

export default WorkflowDeck;
